import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';

import type {
  IncassoDetail,
  IncassoListItem,
  IncassosResponse,
  IncassoStatusType,
} from './endpoints/IncassoEndpoint.types';
import type { CreateIncassoBody, CreateIncassoResponse } from './endpoints/CreateIncassoEndpoint.types';
import type { UpdateIncassoBody, UpdateIncassoResponse } from './endpoints/UpdateIncassoEndpoint.types';
import type { LinkIncassoDocumentResponse } from './endpoints/LinkIncassoDocumentEndpoint.types';

// Cleaner re-exports for consumers (mirrors the old generated-graphql types).
export type { IncassoDetail, IncassoListItem, IncassoStatusType, CreateIncassoBody, UpdateIncassoBody };

export const INCASSO_SWR_KEY = 'incasso';

// ----------------------------------------------------------------------------
// Fetchers
// ----------------------------------------------------------------------------

/**
 * GET /v1/incassos — list with cursor pagination.
 *
 * Mirrors the GraphQL `incassos(id, take)` query: `id` is the BigInt cursor and
 * `take` the page size (negative walks backwards). Returns the raw array so the
 * `usePagination` hook can key off `id` exactly as before.
 */
export async function getIncassos(
  cursor?: number | null,
  take?: number,
  sortBy?: string,
  sortOrder?: string,
): Promise<IncassosResponse> {
  const params = new URLSearchParams();
  if (cursor != null) params.set('id', cursor.toString());
  if (take != null) params.set('take', take.toString());
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);

  const queryString = params.toString();
  const url = queryString ? `/api/v1/incassos?${queryString}` : '/api/v1/incassos';
  return fetchEndpointData<IncassosResponse>(url);
}

export async function getIncasso(id: number | string): Promise<IncassoDetail> {
  return fetchEndpointData<IncassoDetail>(`/api/v1/incassos/${id}`);
}

export async function createIncasso(body: CreateIncassoBody): Promise<CreateIncassoResponse> {
  return fetchEndpointData<CreateIncassoResponse>('/api/v1/incassos', {
    method: 'POST',
    body,
  });
}

export async function updateIncasso(id: number | string, body: UpdateIncassoBody): Promise<UpdateIncassoResponse> {
  return fetchEndpointData<UpdateIncassoResponse>(`/api/v1/incassos/${id}`, {
    method: 'PATCH',
    body,
  });
}

export async function deleteIncasso(id: number | string): Promise<{ id: number }> {
  return fetchEndpointData<{ id: number }>(`/api/v1/incassos/${id}`, {
    method: 'DELETE',
  });
}

export async function linkIncassoDocument(
  id: number | string,
  documentId: string,
): Promise<LinkIncassoDocumentResponse> {
  return fetchEndpointData<LinkIncassoDocumentResponse>(`/api/v1/incassos/${id}/link-document`, {
    method: 'POST',
    body: { documentId },
  });
}

export async function unlinkIncassoDocument(
  id: number | string,
  documentId: string,
): Promise<LinkIncassoDocumentResponse> {
  return fetchEndpointData<LinkIncassoDocumentResponse>(`/api/v1/incassos/${id}/unlink-document`, {
    method: 'POST',
    body: { documentId },
  });
}

// ----------------------------------------------------------------------------
// SWR hooks
// ----------------------------------------------------------------------------

/**
 * Single incasso case.
 */
export function useIncasso(id: number | string) {
  return useSWR([INCASSO_SWR_KEY, id], () => getIncasso(id));
}
